import axios from "axios";

const ENV_URL = process.env.REACT_APP_BACKEND_URL;
const onCustomDomain = !/\.emergent(agent)?\.(host|com)$|^localhost$/.test(window.location.hostname);
// Custom domains route /api to the same backend; staying same-origin keeps login cookies first-party.
const API = `${onCustomDomain ? window.location.origin : ENV_URL}/api`;
const NO_REFRESH = ["/auth/refresh", "/auth/login", "/auth/register", "/auth/google/session"];

export const http = axios.create({ baseURL: API, withCredentials: true });

let refreshing = null;
http.interceptors.response.use(
  (r) => r,
  async (err) => {
    const cfg = err.config;
    if (err.response?.status === 401 && cfg && !cfg._retry && !NO_REFRESH.some((u) => cfg.url.includes(u))) {
      cfg._retry = true;
      refreshing = refreshing || http.post("/auth/refresh").finally(() => { refreshing = null; });
      try {
        await refreshing;
        return http(cfg);
      } catch {
        return Promise.reject(err);
      }
    }
    return Promise.reject(err);
  }
);

export function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message || "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (typeof e?.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  return typeof detail.msg === "string" ? detail.msg : String(detail);
}

const data = (p) => p.then((r) => r.data);
const sitParams = (s) => ({ outs: s.outs, runners: s.runners, hit_type: s.hitType });

export const fetchConfig = () => data(http.get("/config"));
export const fetchDefaultPlays = (sport, level, s) => data(http.get("/plays", { params: { sport, level, ...sitParams(s) } }));

export const api = {
  me: () => data(http.get("/auth/me")),
  login: (body) => data(http.post("/auth/login", body)),
  register: (body) => data(http.post("/auth/register", body)),
  google: (session_id) => data(http.post("/auth/google/session", { session_id })),
  logout: () => data(http.post("/auth/logout")),
  teams: () => data(http.get("/teams")),
  team: (id) => data(http.get(`/teams/${id}`)),
  createTeam: (body) => data(http.post("/teams", body)),
  updateTeam: (id, body) => data(http.patch(`/teams/${id}`, body)),
  deleteTeam: (id) => data(http.delete(`/teams/${id}`)),
  duplicateTeam: (id, body) => data(http.post(`/teams/${id}/duplicate`, body)),
  teamPlays: (id, s) => data(http.get(`/teams/${id}/plays`, { params: sitParams(s) })),
  customPlays: (id) => data(http.get(`/teams/${id}/custom-plays`)),
  savePlay: (id, body) => data(http.put(`/teams/${id}/plays`, body)),
  resetPlay: (id, key) => data(http.delete(`/teams/${id}/plays`, { params: { key } })),
  join: (code) => data(http.get(`/join/${code}`)),
  joinPlays: (code, s) => data(http.get(`/join/${code}/plays`, { params: sitParams(s) })),
  practiceJoin: (code, name) => data(http.post(`/join/${code}/practice/players`, { name })),
  practiceAttempt: (code, body) => data(http.post(`/join/${code}/practice/attempts`, body)),
  practiceStats: (id) => data(http.get(`/teams/${id}/practice-stats`)),
  checkout: (lookup_key) => data(http.post("/payments/checkout", { lookup_key, origin_url: window.location.origin })),
  paymentStatus: (sid) => data(http.get(`/payments/status/${sid}`)),
  cancelPlan: () => data(http.post("/billing/cancel")),
  leaderboard: (code) => data(http.get(`/join/${code}/practice/leaderboard`)),
  joinAssignments: (code) => data(http.get(`/join/${code}/practice/assignments`)),
  assignments: (id) => data(http.get(`/teams/${id}/assignments`)),
  assign: (id, body) => data(http.post(`/teams/${id}/assignments`, body)),
  unassign: (id, aid) => data(http.delete(`/teams/${id}/assignments/${aid}`)),
  contact: (body) => data(http.post("/contact", body)),
  gameday: (id) => data(http.get(`/teams/${id}/gameday`)),
  setGameday: (id, keys) => data(http.put(`/teams/${id}/gameday`, { keys })),
  rotateStaffCode: (id) => data(http.post(`/teams/${id}/staff-code`)),
  lineups: (id) => data(http.get(`/teams/${id}/lineups`)),
  createLineup: (id, body) => data(http.post(`/teams/${id}/lineups`, body)),
  updateLineup: (id, lid, body) => data(http.put(`/teams/${id}/lineups/${lid}`, body)),
  deleteLineup: (id, lid) => data(http.delete(`/teams/${id}/lineups/${lid}`)),
  staffTeam: (code) => data(http.get(`/staff/${code}`)),
  staffPlays: (code, s) => data(http.get(`/staff/${code}/plays`, { params: sitParams(s) })),
  staffLineups: (code) => data(http.get(`/staff/${code}/lineups`)),
  staffGameday: (code) => data(http.get(`/staff/${code}/gameday`)),
  events: (id) => data(http.get(`/teams/${id}/events`)),
  createEvent: (id, body) => data(http.post(`/teams/${id}/events`, body)),
  updateEvent: (id, eid, body) => data(http.put(`/teams/${id}/events/${eid}`, body)),
  deleteEvent: (id, eid) => data(http.delete(`/teams/${id}/events/${eid}`)),
  announcements: (id) => data(http.get(`/teams/${id}/announcements`)),
  createAnnouncement: (id, body) => data(http.post(`/teams/${id}/announcements`, body)),
  deleteAnnouncement: (id, aid) => data(http.delete(`/teams/${id}/announcements/${aid}`)),
  setEventStatus: (id, eid, body) => data(http.put(`/teams/${id}/events/${eid}/status`, body)),
  pickMakeup: (id, eid, oid) => data(http.post(`/teams/${id}/events/${eid}/makeup/${oid}/pick`)),
  makeupVote: (code, eid, body) => data(http.post(`/join/${code}/events/${eid}/makeup-vote`, body)),
  updateRole: (id, rid, applies_to) => data(http.put(`/teams/${id}/volunteers/${rid}`, { applies_to })),
  volunteers: (id) => data(http.get(`/teams/${id}/volunteers`)),
  createRole: (id, body) => data(http.post(`/teams/${id}/volunteers`, body)),
  deleteRole: (id, rid) => data(http.delete(`/teams/${id}/volunteers/${rid}`)),
  removeVolunteer: (id, rid, name) => data(http.delete(`/teams/${id}/volunteers/${rid}/signup`, { params: { name } })),
  volunteerSignup: (code, rid, name) => data(http.post(`/join/${code}/volunteers/${rid}/signup`, { name })),
  volunteerCancel: (code, rid, name) => data(http.delete(`/join/${code}/volunteers/${rid}/signup`, { params: { name } })),
  board: (code) => data(http.get(`/join/${code}/board`)),
  slotSignup: (code, eid, sid, name) => data(http.post(`/join/${code}/events/${eid}/slots/${sid}/signup`, { name })),
  slotCancel: (code, eid, sid, name) => data(http.delete(`/join/${code}/events/${eid}/slots/${sid}/signup`, { params: { name } })),
  rsvp: (code, eid, body) => data(http.post(`/join/${code}/events/${eid}/rsvp`, body)),
};
